import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef } from "react";

// --------------------------------
// Web dashboard client-side load instrumentation
// --------------------------------
//
// Web dashboard block/slicer SQL runs client-side in SQL.js inside a Web Worker,
// serialized through a single FIFO queue. None of it is an HTTP request, so it is
// invisible to Sentry replays and the Network tab — which is exactly why slow /
// "blank screen" loads have been impossible to diagnose from a replay alone.
//
// These helpers add the missing visibility:
//  - Three forced rollup transactions carrying phase-timing attributes → queryable/
//    aggregatable in Performance: `web_dashboard.db_load`, `web_dashboard.runtime_init`,
//    and `web_dashboard.full_load` (navigation start → all blocks rendered; emitted with
//    status `cancelled` when the user reloads/leaves before the load completes, so
//    abandoned loads are measurable instead of silently dropped).
//  - Breadcrumbs (category `web_dashboard.perf`) → attached to replays and any
//    captured error, giving a per-load timeline of what the client was doing.
//
// We intentionally do NOT emit a span per query/block/phase: at load time there is
// no active root span (the pageload transaction has ended), so each would become its
// own unsampled transaction (tracesSampleRate=1.0) rather than nest into a waterfall.
// The breadcrumbs carry the fine-grained timeline; the two transactions carry the
// aggregate breakdown.
//
// Everything here is best-effort: instrumentation must never break a dashboard,
// so every call is guarded and failures are swallowed.

export const PERF_BREADCRUMB_CATEGORY = "web_dashboard.perf";
export const LOAD_SPAN_OP = "web_dashboard.load";

/**
 * Per-query breadcrumbs are noisy (a dashboard can fire dozens of queries) and the
 * Sentry breadcrumb buffer is capped, so only queries at/above this threshold (or
 * that error) get a breadcrumb.
 */
export const SLOW_SQL_BREADCRUMB_MS = 150;

/** Max chars of SQL text kept on breadcrumbs to bound payload size. */
const SQL_TEXT_LIMIT = 200;

/** Coarse statement classification for span names / breadcrumb tags. */
export function classifySql(sql: string): string {
  const s = sql.trimStart().toUpperCase();
  if (s.startsWith("CREATE TEMP TABLE") || s.startsWith("CREATE TEMPORARY TABLE")) {
    return "create_temp_table";
  }
  if (s.startsWith("CREATE TEMP VIEW") || s.startsWith("CREATE TEMPORARY VIEW")) {
    return "create_temp_view";
  }
  if (s.startsWith("CREATE INDEX") || s.startsWith("CREATE UNIQUE INDEX")) {
    return "create_index";
  }
  if (s.startsWith("DROP VIEW")) {
    return "drop_view";
  }
  if (s.startsWith("DROP TABLE")) {
    return "drop_table";
  }
  if (s.startsWith("SELECT")) {
    return "select";
  }
  if (s.startsWith("PRAGMA")) {
    return "pragma";
  }
  return "other";
}

export function truncateSql(sql: string): string {
  const collapsed = sql.replace(/\s+/g, " ").trim();
  return collapsed.length > SQL_TEXT_LIMIT ? `${collapsed.slice(0, SQL_TEXT_LIMIT)}…` : collapsed;
}

export const WEB_DASHBOARD_ID_TAG = "web_dashboard_id";

/**
 * Tags the active Sentry scope with the dashboard id while the viewer is mounted,
 * so errors, replays, and transaction events for this dashboard can be filtered by
 * `web_dashboard_id` in Sentry. (Individual child spans are filtered separately via
 * the `dashboard_id` span attribute set at each span's creation.)
 */
export function useWebDashboardSentryTag(dashboardId: string | undefined): void {
  useEffect(() => {
    if (!dashboardId) {
      return;
    }
    try {
      Sentry.setTag(WEB_DASHBOARD_ID_TAG, dashboardId);
    } catch {
      // Instrumentation must never break the dashboard.
    }
    return () => {
      try {
        Sentry.setTag(WEB_DASHBOARD_ID_TAG, undefined);
      } catch {
        // Swallow.
      }
    };
  }, [dashboardId]);
}

/** Add a guarded perf breadcrumb. Never throws. */
export function addPerfBreadcrumb(message: string, data?: Record<string, unknown>): void {
  try {
    Sentry.addBreadcrumb({
      category: PERF_BREADCRUMB_CATEGORY,
      level: "info",
      message,
      data,
    });
  } catch {
    // Instrumentation must never break the dashboard.
  }
}

/**
 * Record an already-completed phase as a span using explicit wall-clock start/end
 * timestamps (ms since epoch). Used where wrapping the live code in an active
 * `startSpan(cb)` is awkward — e.g. flows with `cancelled` early-returns. Pass
 * `forceTransaction` to make it a root transaction (queryable in Performance even
 * after the pageload transaction has ended).
 */
export function recordCompletedSpan(opts: {
  name: string;
  op: string;
  startMs: number;
  endMs: number;
  forceTransaction?: boolean;
  /** Marks the span with status `cancelled` (user abandoned the operation mid-flight). */
  cancelled?: boolean;
  attributes?: Record<string, string | number | boolean | undefined>;
}): void {
  try {
    const attributes: Record<string, string | number | boolean> = {};
    for (const [k, v] of Object.entries(opts.attributes ?? {})) {
      if (v !== undefined) {
        attributes[k] = v;
      }
    }
    const span = Sentry.startInactiveSpan({
      name: opts.name,
      op: opts.op,
      forceTransaction: opts.forceTransaction,
      startTime: new Date(opts.startMs),
      attributes,
    });
    if (opts.cancelled) {
      // code 2 = span error status; the message becomes the queryable
      // transaction status in Sentry. (SPAN_STATUS_ERROR is not re-exported
      // at runtime by @sentry/react, hence the literal.)
      span?.setStatus({ code: 2, message: "cancelled" });
    }
    span?.end(new Date(opts.endMs));
  } catch {
    // Swallow — see note above.
  }
}

type VisibilityTracker = { wasHidden: () => boolean; stop: () => void };

/**
 * Tracks whether the document was hidden at any point between creation and stop().
 * Phase durations here are wall-clock, so tab-backgrounding and laptop sleep inflate
 * them; the resulting `was_hidden` attribute lets those outliers be filtered out of
 * p50/p95 widgets (`!was_hidden:true`).
 */
export function startVisibilityTracker(): VisibilityTracker {
  let hidden = false;
  const onChange = () => {
    if (document.visibilityState === "hidden") {
      hidden = true;
    }
  };
  try {
    hidden = document.visibilityState === "hidden";
    document.addEventListener("visibilitychange", onChange);
  } catch {
    // Instrumentation must never break the dashboard.
  }
  return {
    wasHidden: () => hidden,
    stop: () => {
      try {
        document.removeEventListener("visibilitychange", onChange);
      } catch {
        // Swallow.
      }
    },
  };
}

/**
 * Start anchor for the `web_dashboard.full_load` transaction. On a hard page load /
 * reload the SDK's `pageload` root span is still active when the viewer mounts, so we
 * anchor at the browser's navigation start — capturing HTML/JS download and parse time
 * that happens before any React code runs. On an SPA route change we anchor at the
 * `navigation` root span's start (the click that initiated the route change). If
 * neither is active (root span already idle-timed-out before mount), fall back to now.
 */
function getFullLoadStart(): { startMs: number; anchor: string } {
  try {
    const activeSpan = Sentry.getActiveSpan();
    const rootSpan = activeSpan ? Sentry.getRootSpan(activeSpan) : undefined;
    if (rootSpan) {
      const { op, start_timestamp: startTimestamp } = Sentry.spanToJSON(rootSpan);
      if (op === "pageload") {
        return { startMs: performance.timeOrigin, anchor: "pageload" };
      }
      if (op === "navigation" && startTimestamp) {
        return { startMs: startTimestamp * 1000, anchor: "navigation" };
      }
    }
  } catch {
    // Swallow — see note above.
  }
  return { startMs: Date.now(), anchor: "mount" };
}

type FullLoadState = {
  dashboardId?: string;
  startMs: number;
  anchor: string;
  emitted: boolean;
  visibility: VisibilityTracker;
};

/**
 * Owns the `web_dashboard.full_load` transaction: navigation start → all blocks
 * painted, as one queryable root transaction per dashboard visit. Unlike db_load /
 * runtime_init (which only exist for loads that complete), an abandoned load — user
 * reloads or leaves mid-load — is emitted too, with status `cancelled` and duration
 * "how long they waited before giving up", so slow dashboards that users bail on are
 * measurable instead of invisible. Abandonment has three exits, all handled here:
 * reload/tab close (`pagehide`; the SDK's keepalive transport usually survives page
 * teardown), SPA navigation away (unmount cleanup), and switching to a different
 * dashboard without unmounting (the caller invokes `abandonAndReset`).
 *
 * `isSuppressed` gates every emission — build-flow sessions must not report, since
 * their duration would include planning + build time, not load time.
 */
export function useFullLoadTransaction(opts: {
  dashboardId: string | undefined;
  isSuppressed: () => boolean;
  getDbLoadMs: () => number | null;
}): {
  markComplete: (numDataBlocks: number) => void;
  abandonAndReset: (dashboardId: string | undefined) => void;
} {
  // Initialized during the first render (not in an effect) so the anchor can still
  // read the SDK's active pageload/navigation root span before it idle-times-out.
  const stateRef = useRef<FullLoadState | null>(null);
  if (stateRef.current === null) {
    stateRef.current = {
      ...getFullLoadStart(),
      dashboardId: opts.dashboardId,
      emitted: false,
      visibility: startVisibilityTracker(),
    };
  }
  const optsRef = useRef(opts);
  optsRef.current = opts;

  // Emits at most once per dashboard visit. Reads only refs so the listeners below
  // hold a stable reference.
  const emit = useCallback((emitOpts: { cancelled: boolean; numDataBlocks?: number }) => {
    const state = stateRef.current;
    if (!state || state.emitted || optsRef.current.isSuppressed()) {
      return;
    }
    state.emitted = true;
    state.visibility.stop();
    recordCompletedSpan({
      name: "web_dashboard.full_load",
      op: LOAD_SPAN_OP,
      startMs: state.startMs,
      endMs: Date.now(),
      forceTransaction: true,
      cancelled: emitOpts.cancelled,
      attributes: {
        dashboard_id: state.dashboardId,
        anchor: state.anchor,
        cancelled: emitOpts.cancelled,
        was_hidden: state.visibility.wasHidden(),
        db_load_ms: optsRef.current.getDbLoadMs() ?? undefined,
        num_data_blocks: emitOpts.numDataBlocks,
      },
    });
  }, []);

  useEffect(() => {
    const onPageHide = () => emit({ cancelled: true });
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [emit]);
  useEffect(
    () => () => {
      emit({ cancelled: true });
      // If emission was suppressed (build flow), the tracker is still live — stop it.
      stateRef.current?.visibility.stop();
    },
    [emit]
  );

  const markComplete = useCallback(
    (numDataBlocks: number) => emit({ cancelled: false, numDataBlocks }),
    [emit]
  );

  const abandonAndReset = useCallback(
    (dashboardId: string | undefined) => {
      emit({ cancelled: true });
      stateRef.current?.visibility.stop();
      stateRef.current = {
        ...getFullLoadStart(),
        dashboardId,
        emitted: false,
        visibility: startVisibilityTracker(),
      };
    },
    [emit]
  );

  return { markComplete, abandonAndReset };
}
